const MailLink = ({ address }: { address: string }) => (
  <a href={`mailto:${address}`}>{address}</a>
);

/**
 * Privacy notice for the Intelligent Learning Platform — shown on the
 * Datenschutz page.
 */
const DatenschutzNotice = () => (
  <>
    <h2>I. Verantwortlicher und Datenschutzbeauftragter</h2>
    <p>
      Verantwortliche für die Verarbeitung personenbezogener Daten bei Nutzung
      der ILP ist:
    </p>
    <address className="not-italic">
      GFN GmbH
      <br />
      Am Taubenfeld 10
      <br />
      69123 Heidelberg
    </address>
    <p>
      Telefon: +49 6221 8322-0
      <br />
      Telefax: +49 6221 8322-88
      <br />
      E-Mail: <MailLink address="info@gfn.de" />
    </p>
    <p>Unser Datenschutzbeauftragter ist:</p>
    <address className="not-italic">
      Amadeus Fire AG
      <br />
      Datenschutzbeauftragter
      <br />
      Hanauer Landstraße 160
      <br />
      60314 Frankfurt am Main
    </address>
    <p>
      E-Mail: <MailLink address="datenschutzbeauftragter@amadeus-fire.de" />
    </p>
    <p>
      Die ILP wird von der Amadeus Fire EduTech GmbH bereitgestellt und
      betrieben und von der GFN für Weiterbildungsmaßnahmen eingesetzt.
    </p>

    <h2>
      II. Verarbeitung personenbezogener Daten nach Zwecken und Rechtsgrundlagen
    </h2>
    <p>
      Wir erheben und verarbeiten deine Daten ausschließlich zu bestimmten
      Zwecken. Diese können sich aus technischen Notwendigkeiten, vertraglichen
      und gesetzlichen Erfordernissen oder einer von dir erteilten Einwilligung
      ergeben. Folgende personenbezogene Daten werden bei Nutzung der Webseite
      zu folgenden Zwecken verarbeitet:
    </p>

    <h3>1. Anmeldung und Kursverwaltung</h3>
    <p>
      Für die Anmeldung und Nutzung der Plattform verarbeiten wir insbesondere
      Vor- und Nachname, E-Mail-Adresse, Spracheinstellung, Kurs- und
      Terminzuordnungen, Benutzerrolle sowie Sitzungs- und
      Authentifizierungsdaten.
    </p>
    <p>
      Die Daten können aus dem Verwaltungssystem TC-Manager übernommen werden.
      Die Verarbeitung erfolgt zur Bereitstellung der Plattform und zur
      Durchführung deines Kurses auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO.
    </p>

    <h3>2. Lernaktivitäten und Lernfortschritt</h3>
    <p>
      Während der Nutzung der Plattform können insbesondere folgende Daten
      verarbeitet werden: Beginn und Ende von Lernsitzungen, aufgerufene Inhalte
      sowie Klick- und Navigationsvorgänge, Bearbeitungs- und Verweildauer,
      Fortschritt und Abschluss von Lernmodulen, Quiz-Ergebnisse und
      Wiederholungsversuche, Freitextantworten, Arbeitsblätter und
      Selbstreflexionen sowie Lösungen zu Programmieraufgaben.
    </p>
    <p>
      Diese Daten werden zur Durchführung des Kurses, zur Darstellung des
      Lernfortschritts und zur Erstellung von Teilnahme- oder
      Leistungsnachweisen verarbeitet. Rechtsgrundlage ist grundsätzlich Art. 6
      Abs. 1 lit. b DSGVO.
    </p>
    <p>
      Die Freitext- oder Programmieraufgaben können mithilfe eines KI-Systems
      ausgewertet werden, um dir Feedback zu deinen Antworten zu geben. Bei
      Zweifeln an einer KI-generierten Bewertung kannst du dich jederzeit an
      deine Ansprechpartner bei der GFN wenden, um eine menschliche Überprüfung
      der Bewertung zu erhalten.
    </p>
    <p>
      Hinweis: Bitte trage in Freitext- oder Reflexionsfelder keine
      personenbezogenen Daten über dich oder andere Personen und insbesondere
      keine sensiblen Informationen, beispielsweise Gesundheitsdaten, ein.
    </p>

    <h3>3. Qualitätssicherung der Plattform</h3>
    <p>
      Bestimmte Nutzungsdaten, beispielsweise Navigationsvorgänge, Verweildauer
      oder Scroll-Tiefe, können zur technischen und inhaltlichen Verbesserung
      der Plattform ausgewertet werden. Die Daten werden aggregiert verarbeitet,
      sodass kein Rückschluss auf einzelne Personen besteht.
    </p>
    <p>
      Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Unser berechtigtes
      Interesse besteht in der Qualitätssicherung sowie der Verbesserung der
      technischen und didaktischen Gestaltung der Plattform.
    </p>

    <h3>4. Unterstützungsbedarf und Kursabbruchrisiko</h3>
    <p>
      Aus Lernaktivitäten und Lernfortschritt können Hinweise auf einen
      möglichen Unterstützungsbedarf oder ein erhöhtes Kursabbruchrisiko
      abgeleitet werden. Diese Hinweise sollen Lernbegleitern ermöglichen,
      frühzeitig geeignete Unterstützung anzubieten.
    </p>
    <p>
      Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Unser berechtigtes
      Interesse besteht in der Verbesserung der Betreuung, der optimalen
      Unterstützung sowie der Prävention von Kursabbrüchen.
    </p>

    <h3>5. Freiwilliger KI-Lernassistent</h3>
    <p>
      Die Plattform bietet zusätzlich den Intelligent Learning Assistant, kurz
      ILA, an. Der ILA ist ein KI-basierter Chatbot, der Fragen zu Kursinhalten
      beantworten und beim Lernen unterstützen kann.
    </p>
    <p>
      Bei der Nutzung werden insbesondere deine Chat-Eingaben / Prompts und die
      Antworten des ILA verarbeitet. Die Chat-Inhalte nur während der jeweiligen
      Unterhaltung verarbeitet und nicht über die Sitzung hinaus als Chatverlauf
      gespeichert. Lernbegleiter erhalten keinen Zugriff auf die Inhalte deiner
      Kommunikation mit dem ILA.
    </p>
    <p>
      Hinweis: Bitte gib in den ILA keine personenbezogenen Daten über dich oder
      andere Personen und insbesondere keine sensiblen Informationen,
      beispielsweise Gesundheitsdaten, ein.
    </p>
    <p>
      Die Nutzung des ILA ist freiwillig und für den erfolgreichen Abschluss des
      Kurses nicht erforderlich. Rechtsgrundlage ist deine Einwilligung nach
      Art. 6 Abs. 1 lit. a DSGVO, die du vor der ersten Nutzung des ILA erteilst
      und jederzeit per Mail an <MailLink address="datenschutz@gfn.de" />{" "}
      widerrufen kannst.
    </p>

    <h3>6. Technische Betriebs- und Sicherheitsdaten</h3>
    <p>
      Zum sicheren Betrieb der Plattform werden insbesondere Anwendungs- und
      Fehlerprotokolle, Zugriffs- und Audit-Logs sowie verschlüsselte
      Sicherungskopien verarbeitet.
    </p>
    <p>
      Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO.
      Unser berechtigtes Interesse besteht in der Gewährleistung von
      IT-Sicherheit, Betriebssicherheit, Fehlerbehebung und
      Missbrauchserkennung.
    </p>

    <h2>III. Empfänger und Dienstleister; Drittlandverarbeitung</h2>
    <p>
      Zugriff auf deine Daten erhalten nur Personen, die diese zur Durchführung
      und Betreuung des Kurses oder zum technischen Betrieb der Plattform
      benötigen.
    </p>
    <p>
      Je nach Verarbeitungsfall können insbesondere folgende Empfänger oder
      Dienstleister eingebunden sein:
    </p>
    <ul>
      <li>
        Amadeus Fire EduTech GmbH, Hanauer Landstr. 160, 60314 Frankfurt am Main
        als technischer Betreiber,
      </li>
      <li>
        OpenAI für KI-gestützte Funktionen (OpenAI Ireland Ltd., 1st Floor, The
        Liffey Trust Centre, 117-126 Sheriff Street Upper, Dublin 1, D01 YC43,
        Irland – Mutterkonzern mit Sitz in den USA),
      </li>
      <li>
        Hosting-, Speicher-, Fehleranalyse- und IT-Sicherheitsdienstleister
        sowie
      </li>
      <li>Lernbegleiter im erforderlichen Umfang.</li>
    </ul>
    <p>
      Dienstleister werden, soweit erforderlich, auf Grundlage eines Vertrags
      zur Auftragsverarbeitung nach Art. 28 DSGVO eingesetzt.
    </p>

    <h2>8. Speicherdauer</h2>
    <p>
      GFN verarbeitet und speichert die hier genannten personenbezogenen Daten
      nur für den Zeitraum, der zur Erreichung des Speicherungszwecks oder
      aufgrund von Gesetzen oder sonstigen rechtlichen Verpflichtungen
      erforderlich ist.
    </p>
    <p>
      Liegen keine gesetzlichen Fristen vor, bemisst sich das Kriterium für die
      Dauer der Speicherung von personenbezogenen Daten an den Zwecken der
      Erhebung, z. B. der Erforderlichkeit zur Vertragserfüllung oder unserem
      berechtigten Interesse. Die Daten werden mit Zweckerfüllung oder aufgrund
      der Löschfrist routinemäßig und entsprechend den gesetzlichen Vorschriften
      gesperrt oder gelöscht.
    </p>
    <p>
      Grundsätzlich ergibt sich daraus eine Speicherdauer für die auf der ILP
      verarbeiteten Daten für bis zu 36 Monate im Anschluss an die
      Nutzungsdauer.
    </p>

    <h2>V. Betroffenenrechte</h2>
    <p>
      Anträge zur Ausübung deiner Betroffenenrechte sollen bitte an{" "}
      <MailLink address="datenschutz@gfn.de" /> oder alternativ bei der oben
      angegebenen Anschrift des Verantwortlichen persönlich, postalisch oder
      telefonisch erfolgen. Folgende Rechte bestehen unter bestimmten
      Voraussetzungen:
    </p>
    <p>
      Recht auf Auskunft nach Art. 15 DSGVO: über alle vorhandenen Kategorien
      personenbezogener Daten und Verarbeitungen, deren Verarbeitungszwecke,
      Kategorien von Empfänger:innen, falls möglich die geplante Dauer der
      Verarbeitung oder Speicherung sowie Angaben über Übermittlungen von Daten
      in ein Drittland außerhalb der EU und dazugehörige Vertragsklauseln, auf
      welche die Übermittlung gestützt wird.
    </p>
    <p>
      Recht auf Berichtigung nach Art. 16 DSGVO: um deine personenbezogenen
      Daten, die unrichtig oder veraltetet sind, zu korrigieren oder zu
      vervollständigen.
    </p>
    <p>
      Recht auf Löschung nach Art. 17 DSGVO: um deine personenbezogenen Daten
      unverzüglich zu löschen. Dies gilt jedoch nur für Daten, die nicht mehr
      für den erhobenen Zweck notwendig sind, oder wenn du eine Einwilligung
      widerrufst und keine gesetzlichen oder unionsrechtlichen
      Aufbewahrungsfristen bestehen, die uns dazu verpflichten, die Daten
      weiterhin aufzubewahren.
    </p>
    <p>
      Recht auf Einschränkung der Verarbeitung nach Art. 18 DSGVO: wenn Zweifel
      an der Richtigkeit der Daten bestehen, die Verarbeitung unrechtmäßig ist,
      die Daten für den ursprünglichen Zweck nicht mehr benötigt werden oder du
      nach Art. 21 DSGVO widersprichst. Die Einschränkung führt dazu, dass
      vorhandene Daten nur noch mit deiner Einwilligung, zur Geltendmachung,
      Ausübung oder Verteidigung von Rechtsansprüchen, zum Schutz der Rechte
      einer anderen Person, oder aus Gründen eines wichtigen öffentlichen
      Interesses verarbeitet werden dürfen, mit Ausnahme der Speicherung.
    </p>
    <p>
      Recht auf Datenübertragbarkeit nach Art. 20 DSGVO: wenn die Verarbeitung
      aufgrund deiner Einwilligung oder zur Erfüllung eines Vertrags und mit
      Hilfe automatisierter Verfahren erfolgt, damit du deine Daten in einem
      strukturierten, gängigen und maschinenlesbaren Format zur Verfügung
      gestellt bekommst oder uns veranlassen kannst, die Daten direkt an einen
      anderen Verantwortlichen zu übermitteln, sofern dies technisch machbar
      ist.
    </p>
    <p>
      Recht auf Widerspruch nach Art. 21 DSGVO: um eine Verarbeitung, die zur
      Wahrnehmung einer Aufgabe im öffentlichen Interesse, wegen eines
      bestehenden berechtigten Interesses oder aus forschungs-statistischen
      Zwecken der GFN erfolgt, zu beenden, es sei denn, es bestehen zwingende
      schutzwürdige Gründe für die Verarbeitung, die überwiegen, oder die
      Verarbeitung dient der Geltendmachung, Ausübung oder Verteidigung von
      Rechtsansprüchen.
    </p>
    <p>
      Recht gegen automatisierte Entscheidungen und Profiling nach Art. 22
      DSGVO: um nicht einer ausschließlich auf einer automatisierten
      Verarbeitung beruhenden Entscheidung unterworfen zu werden, die dir
      gegenüber rechtliche Wirkung entfaltet oder dich in ähnlicher Weise
      erheblich beeinträchtigt. Automatisierte Verarbeitungen sind solche, die
      vollkommen ohne jegliche Mitwirkung von Menschen erfolgen, bei denen deine
      personenbezogenen Daten automatisiert bewertet werden, z. B. automatische
      Profilbildungen (sog. Profiling). Sie unterliegen besonderen
      Datenschutzanforderungen und dürfen in der Regel nur eingesetzt werden,
      wenn du eingewilligt hast oder es zur Erfüllung eines Vertrags oder von
      Rechtsvorschriften erforderlich ist. Automatisierte Entscheidungsfindungen
      werden bei GFN aktuell nicht eingesetzt.
    </p>
    <p>
      Recht auf Widerruf einer datenschutzrechtlichen Einwilligung nach Art. 7
      DSGVO: um von dir erteilte Einwilligungen zu widerrufen und die hiervon
      erfassten Verarbeitungen zu beenden. Bis zum Widerruf erfolgte
      Verarbeitungen werden hiervon nicht berührt. Einwilligungen für Cookies
      können im Cookie-Banner durch Deaktivieren widerrufen werden.
    </p>
    <p>
      Recht auf Beschwerde nach Art. 77 DSGVO: um dich schriftlich, telefonisch
      oder persönlich bei einer Datenschutzaufsichtsbehörde über die
      Verarbeitung deiner Daten durch uns zu beschweren.
    </p>
  </>
);

export default DatenschutzNotice;
